// src/encounter/risk/lib/risk-assessment-types.ts
// Shape of the Risk-tab data. Field names mirror the REAL EncounterMetaData wire contract
// (data-layer handoff, 2026-09-13) — not the earlier, less precise "persistence field names"
// list from the original UI spec. Two differences worth flagging explicitly:
//   - The wire Fall entry has NO `comment` field (the original UI spec listed one). Dropped —
//     wire fidelity wins; nothing in this pass's render order surfaced a comment control anyway.
//   - Fall's `items` are NESTED CATEGORIES (one per Morse question, each holding one row per
//     option with its own fixed `score` and a `status` of `'yes' | 'none'`) — not a flat
//     "one selected score per question" list. `totalPoints()` (morse-score.ts) sums every
//     nested option whose `status === 'yes'`, which is the literal wording of the scoring rule.
//
// This module owns SHAPE + local (in-memory) transforms only — no I/O. See:
//   - `risk-wire.ts` for the local <-> wire payload mapping
//   - `services/use-risk-assessments.ts` for the network-backed hook that replaces the earlier
//     QuestionnaireResponse-backed `risk-persistence.ts` (removed — Risk no longer writes any
//     `risk-*` group into the shared QuestionnaireResponse).
import { clinicWallClockStamp } from '@/shared/lib/clinic-time';
import { MORSE_QUESTIONS } from './morse-questions';
import { PAIN_FIELDS } from './pain-fields';

/** One option row inside a Fall category — `t` is an UNVERIFIED wire field (its meaning was not
 *  given by the data-layer handoff). Passed through unchanged when the server sent one; never
 *  fabricated for a locally-created answer. Flag for confirmation before this ships. */
export interface FallCategoryOptionAnswer {
  readonly id: string;
  readonly title: string;
  readonly score: number;
  readonly status: 'yes' | 'none';
  readonly t?: unknown;
}

/** One Morse question's full set of options (one category per `MORSE_QUESTIONS` entry). */
export interface FallCategoryAnswer {
  readonly id: string;
  readonly title: string;
  readonly items: readonly FallCategoryOptionAnswer[];
}

/** One Fall Assessment entry. Field set mirrors the wire contract exactly (see module header). */
export interface FallAssessmentEntry {
  readonly id: string;
  readonly notePlainText: string;
  readonly title: string;
  readonly items: readonly FallCategoryAnswer[];
  readonly isPresent: boolean;
  readonly isCompleted: boolean;
  /** ISO-8601, or `undefined` — the wire OMITS this key entirely rather than sending `null`;
   *  see `risk-wire.ts` `encodeFallEntry`. */
  readonly createdOn: string | undefined;
}

/** A pain chip-field's stored value: single-select fields store one string, multi-select an array. */
export type PainFieldValue = string | string[];

/**
 * One Pain Assessment entry. Field set mirrors the wire contract. `items` here is the object map
 * this repo already used pre-handoff (`fieldId -> value`) — the handoff's "Nested category" shape
 * was illustrated against Fall's SCORED categories; no analogous nested shape was given for Pain's
 * un-scored chip fields, and forcing the scored shape onto them (`score` would be meaningless)
 * would be inventing a contract, not reading one. UNVERIFIED — confirm the real Pain `items` wire
 * shape before this ships; `risk-wire.ts` isolates the mapping so a correction is one function.
 */
export interface PainAssessmentEntry {
  readonly id: string;
  readonly title: string;
  readonly items: Readonly<Record<string, PainFieldValue>>;
  readonly isPresent: boolean;
  readonly notePlainText: string;
  readonly isEnterNumber: boolean;
  readonly onset: string;
  readonly address: string;
  readonly painScore: string | undefined;
  readonly isCompleted: boolean;
  readonly pharmacologicalInterventions: string;
  readonly createdOn: string | undefined;
}

let seq = 0;
function nextId(prefix: string): string {
  seq += 1;
  return `${prefix}-${Date.now()}-${seq}`;
}

/** Stable option id — a slug of its label, unique within its category. */
function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/**
 * A fresh, un-started Fall Assessment. Founder ruling, 7 Oct 2026 (licensing: CDC STEADI and NRS 0–10 replace Morse and
 * Wong-Baker everywhere; encounter authority "RULED by Samer, 7 Oct 2026"): a new encounter carries NO Morse rows — it
 * used to mint the 8 empty Morse categories and save them with every new record. `risk-wire.ts` reads and writes an
 * empty list. Historic records keep their Morse rows (read-only history).
 */
export function emptyFallAssessment(): FallAssessmentEntry {
  return {
    id: nextId('fall'),
    notePlainText: '',
    title: 'Fall Assessment',
    items: [],
    isPresent: false,
    isCompleted: false,
    createdOn: undefined,
  };
}

/** A Morse category as an option list (the shape the retired card wrote) — kept for the OFF path, which can still be
 *  served an old Morse template until the template is replaced. Never used for a new record's defaults. */
export function morseCategory(index: number): FallAssessmentEntry['items'][number] | undefined {
  const q = MORSE_QUESTIONS[index];
  if (!q) return undefined;
  return {
    id: q.id,
    title: q.label,
    items: q.options.map((opt) => ({ id: slug(opt.label), title: opt.label, score: opt.score, status: 'none' as const })),
  };
}

/** A fresh, un-started Pain Assessment. */
export function emptyPainAssessment(): PainAssessmentEntry {
  const items: Record<string, PainFieldValue> = {};
  for (const f of PAIN_FIELDS) {
    if (f.storesTo) continue; // onset/address/pharmacologicalInterventions live on named fields
    items[f.id] = f.type === 'chip-multi' ? [] : '';
  }
  return {
    id: nextId('pain'),
    title: 'Pain Assessment',
    items,
    isPresent: false,
    notePlainText: '',
    isEnterNumber: false,
    onset: '',
    address: '',
    painScore: undefined,
    isCompleted: false,
    pharmacologicalInterventions: '',
    createdOn: undefined,
  };
}

/** START: prepend a fresh, isPresent=true entry to the front of the stack (index 0 = current). */
export function startFallAssessment(list: readonly FallAssessmentEntry[]): FallAssessmentEntry[] {
  return [{ ...emptyFallAssessment(), isPresent: true }, ...list];
}

export function startPainAssessment(list: readonly PainAssessmentEntry[]): PainAssessmentEntry[] {
  return [{ ...emptyPainAssessment(), isPresent: true }, ...list];
}

/** DONE: mark entry 0 completed with a createdOn timestamp — LOCAL ONLY, no network call (the
 *  batched save fires later; see `services/use-risk-assessments.ts`).
 *  PROD-3065a (switch OFF too, coordinator ruling 7 Oct: a defect, not intended): `createdOn` is Flutter's own
 *  record-time format on the clinic clock (`clinicWallClockStamp`, Asia/Dubai, `yyyy-MM-dd HH:mm:ss.SSS`, no zone) —
 *  the UTC `toISOString()` read four hours early in Flutter. Set once: a record already completed keeps its time. */
function completeHead<T extends { readonly isCompleted: boolean; readonly createdOn: string | undefined }>(head: T): T {
  return head.isCompleted ? head : { ...head, isCompleted: true, createdOn: clinicWallClockStamp(new Date()) };
}

export function completeFallAssessment(list: readonly FallAssessmentEntry[]): FallAssessmentEntry[] {
  if (list.length === 0) return list as FallAssessmentEntry[];
  const [head, ...rest] = list;
  return [completeHead(head), ...rest];
}

export function completePainAssessment(list: readonly PainAssessmentEntry[]): PainAssessmentEntry[] {
  if (list.length === 0) return list as PainAssessmentEntry[];
  const [head, ...rest] = list;
  return [completeHead(head), ...rest];
}

/** DELETE: removes index 0, LOCALLY — becomes durable via the next batched save. An empty
 *  result is replaced with one fresh placeholder record; the list is never allowed to go empty. */
export function deleteHead<T>(list: readonly T[], makeEmpty: () => T): T[] {
  const rest = list.slice(1);
  return rest.length > 0 ? rest : [makeEmpty()];
}
